import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextLink from 'LuxKit/TextLink'
import Tooltip from 'LuxKit/Tooltip'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import focusStyle from 'styles/utils/focusStyle'
import { tierGradientBackgroundStyle, tierNormalContrastBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import { arrayToObject, nonNullable } from 'lib/array/arrayUtils'
import LoyaltyAccountTileCircularProgressBar from 'loyalty/components/LoyaltyAccountTileCircularProgressBar'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import LoyaltyDrawerTierCard from 'loyalty/components/LoyaltyDrawerTierCard'
import LoyaltyDrawerCongratulationsBanner from 'loyalty/components/LoyaltyDrawerCongratulationsBanner'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useLoyaltyTierProgressionMode from 'loyalty/hooks/useLoyaltyTierProgressionMode'
import useCurrentMembershipYear from 'loyalty/hooks/useCurrentMembershipYear'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import { useCallback } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import AccountDrawerBenefitHighlightItem from './AccountDrawerBenefitHighlightItem'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineMobileAndroidIcon from 'LuxKit/Icons/line/LineMobileAndroidIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import CopyToClipboardClickableIcon from 'checkout/Components/Confirmation/BookingDetailsV2/CopyToClipboardClickableIcon'
import { FormattedMessage, useIntl } from 'react-intl'
import AccountMenuLoyaltyInfiniteDetailsTile from './AccountMenuLoyaltyInfiniteDetailsTile'

const Container = styled(Clickable)`
  padding: ${rem(12)} ${rem(16)};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}

  &:focus-visible {
    ${focusStyle}
  }
`

const ProgressBarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-self: stretch;
  padding-bottom: ${rem(4)};
`

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  line-height: 1;
`

const TierDivider = styled(Divider)`
  opacity: 0.5;
  ${tierNormalContrastBackgroundStyle}
`

const TIER_BENEFITS: Record<App.LoyaltyTier, Array<App.LoyaltyBenefitType>> = {
  bronze: ['esim_package'],
  silver: ['hotel_upgrade', 'lounge_pass', 'discounted_insurance', 'esim_package'],
  gold: ['hotel_upgrade', 'airport_transfer', 'lounge_pass', 'esim_package'],
  platinum: ['airport_transfer', 'hotel_upgrade', 'lounge_pass', 'esim_package'],
  /** @TODO update with correct values */
  infinite: ['airport_transfer', 'hotel_upgrade', 'lounge_pass', 'esim_package'],
}

const BENEFIT_ICONS: Utils.PartialRecord<App.LoyaltyBenefitType, React.ReactElement> = {
  lounge_pass: <LineChairWithAirplaneIcon />,
  airport_transfer: <LineTaxiIcon />,
  hotel_upgrade: <LineCrownIcon />,
  discounted_insurance: <LineShieldCheckIcon />,
  esim_package: <LineMobileAndroidIcon />,
}

interface BenefitConfigItem {
  code: App.LoyaltyBenefitType
  displayInfo: {
    title: string
    icon: React.ReactElement
  }
}

function getTierBenefitDisplayInfo(tier: App.LoyaltyTier, benefitTitles: Utils.PartialRecord<App.LoyaltyBenefitType, string>): Array<BenefitConfigItem> {
  const tierBenefits = TIER_BENEFITS[tier]
  const displayInfoItems = tierBenefits.map(code => {
    const icon = BENEFIT_ICONS[code]
    const title = benefitTitles[code]
    return icon && title ? { code, displayInfo: { title, icon } } : undefined
  })
  return nonNullable(displayInfoItems)
}

interface Props {
  onLinkClick: () => void
  loyaltyAccountData: App.LoyaltyAccount
}

function AccountMenuLoyaltyHasAccountDetailsTile(props: Props) {
  if (props.loyaltyAccountData.tier === 'infinite') {
    return <AccountMenuLoyaltyInfiniteDetailsTile {...props} />
  }

  return <AccountMenuLoyaltyProgressiveTierDetailsTile {...props} />
}

function AccountMenuLoyaltyProgressiveTierDetailsTile({ onLinkClick, loyaltyAccountData }: Props) {
  const tier = loyaltyAccountData.tier
  const benefits = loyaltyAccountData.benefits
  const points = loyaltyAccountData.points
  const membershipNumber = `${loyaltyAccountData.accountNumber}`
  const intl = useIntl()

  const [_tier, tierName, _nextTier, nextTierName] = useCurrentLoyaltyTier()
  const {
    unlockCardTier,
    effectiveRetainMode,
    canToggleView,
    showUpgradeView,
    setShowUpgradeView,
    targetCredits,
    targetNights,
    creditsRemaining,
    nightsRemaining,
    hasRetainedTier,
    statusCreditsProgress,
    nightsProgress,
  } = useLoyaltyTierProgressionMode()
  const membershipYear = useCurrentMembershipYear()
  const membershipYearEnd = membershipYear?.end.toISOString()
  const retainVariant = membershipYear?.retainVariant ?? 'current-year'
  const [pendingPoints] = useCurrentLoyaltyPendingPoints()

  const isEsimEnabled = useAppSelector(getIsLoyaltyEsimEnabled)

  const benefitsMap = arrayToObject(benefits, benefit => benefit.code)
  const benefitTitles: Utils.PartialRecord<App.LoyaltyBenefitType, string> = {
    lounge_pass: intl.formatMessage({
      id: 'nstaLcaEql5nbBt0YSb0',
      defaultMessage: 'Free airport lounge passes',
      description: 'Loyalty benefit title for complimentary airport lounge access',
    }),
    airport_transfer: intl.formatMessage({
      id: 'a_r_yRtiFBNZJmPGvv6L',
      defaultMessage: 'Free airport chauffeur service',
      description: 'Loyalty benefit title for complimentary airport chauffeur transfer',
    }),
    hotel_upgrade: intl.formatMessage({
      id: 'cpTdAkrvd-S0kSm8itsQ',
      defaultMessage: 'Free room or package upgrade',
      description: 'Loyalty benefit title for complimentary room or package upgrade',
    }),
    discounted_insurance: intl.formatMessage({
      id: 'mnLcU6wWtt1XWHGZtGNZ',
      defaultMessage: 'Discounted travel insurance',
      description: 'Loyalty benefit title for discounted travel insurance',
    }),
    esim_package: intl.formatMessage({
      id: 'hGxMxTWCz_tuTCUIKTxK',
      defaultMessage: 'Free international data roaming',
      description: 'Loyalty benefit title for eSIM data package in account drawer',
    }),
  }

  const membershipLabel = intl.formatMessage(
    {
      id: 'EmBa2cCCza9gGbJVI4l1',
      defaultMessage: 'Membership {membershipNumber}',
      description: 'Loyalty membership number display in account menu element',
    },
    { membershipNumber },
  )
  const copyMembershipNumberLabel = intl.formatMessage({
    id: 'gkoIBvPTDxg5zeyp1z9S',
    defaultMessage: 'Copy membership number',
    description: 'Aria label for button to copy loyalty membership number',
  })
  const ptsLabel = intl.formatMessage({
    id: 'sUWfy3VsXfZr9Av3nmPz',
    defaultMessage: 'pts',
    description: 'Abbreviated label for loyalty points in account drawer',
  })
  const statusCreditsLabel = intl.formatMessage({
    id: '-EhO0xPNWFgohnsq2Oiy',
    defaultMessage: 'Status Credits',
    description: 'Label for status credits progress in loyalty account tile',
  })
  const statusCreditsPendingTooltip = intl.formatMessage({
    id: 'X9h8QWFz-ORlSsqCB8u-',
    defaultMessage: 'Pending status credits may take up to 24hrs to appear',
    description: 'Tooltip explaining pending status credits delay in loyalty account tile',
  })
  const nightsLabel = intl.formatMessage({
    id: 'x5UY9HsF8S3OtAjA4Q5D',
    defaultMessage: 'Nights',
    description: 'Form field label for nights progress in loyalty account tile',
  })
  const nightsPendingTooltip = intl.formatMessage({
    id: 'Z4G3K4vMzZnF3B4TNC2R',
    defaultMessage: 'Pending nights may take up to 24hrs to appear',
    description: 'Tooltip explaining pending nights delay in loyalty account tile',
  })
  const topBenefitsLabel = intl.formatMessage({
    id: 'Q1EO2NcxyI1uILATavuU',
    defaultMessage: 'Your top benefits',
    description: 'Section heading for top loyalty membership benefits',
  })
  const viewMembershipLabel = intl.formatMessage({
    id: 'wx0sQZ1Mxh1_2NkmaUra',
    defaultMessage: 'View membership',
    description: 'Hyperlink text to view full loyalty membership details',
  })

  const tierBenefitsDisplayInfo = getTierBenefitDisplayInfo(tier, benefitTitles)

  const validBenefitsDisplayInfo = nonNullable(
    tierBenefitsDisplayInfo.map(displayInfo => {
      if (displayInfo.code === 'esim_package' && !isEsimEnabled) return undefined
      return benefitsMap[displayInfo.code] ? displayInfo : undefined
    }),
  )

  const handleCopyClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const currentStatusCredits = loyaltyAccountData.statusCredits
  const currentNights = loyaltyAccountData.numberOfNights

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <Container
      to={`/${LOYALTY_LANDING_PAGE}`}
      onClick={onLinkClick}
      className={clsx(`tier-${tier}`)}
    >
      <VerticalSpacer gap={20}>
        <VerticalSpacer gap={4}>
          <Group direction="horizontal" horizontalAlign="space-between">
            <LoyaltyProgramNameLogo width={72} />
            {tier && <BodyText variant="medium" format="uppercase">{getLoyaltyTierDisplayName(tier)}</BodyText>}
          </Group>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <Caption variant="large">{membershipLabel}</Caption>
              <CopyToClipboardClickableIcon
                value={membershipNumber}
                onCopyClick={handleCopyClick}
                ariaLabel={copyMembershipNumberLabel}
              />
            </Group>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <Heading variant="heading6">
                <FormatDecimal value={points} />
              </Heading>
              <Caption variant="medium" weight="normal">{ptsLabel}</Caption>
            </Group>
          </Group>
        </VerticalSpacer>

        {/* Circular progress bars */}
        <ProgressBarsContainer>
          <Group direction="vertical" horizontalAlign="center" gap={4}>
            <LoyaltyAccountTileCircularProgressBar
              progress={statusCreditsProgress}
              tier={tier}
            >
              <Group direction="vertical" horizontalAlign="center" gap={2}>
                <BodyText variant="medium" weight="bold">
                  <FormatDecimal value={currentStatusCredits} />
                </BodyText>
                <TierDivider className={`tier-${tier}`} kind="primary" />
                <Caption variant="small">
                  <FormatDecimal value={targetCredits} />
                </Caption>
              </Group>
            </LoyaltyAccountTileCircularProgressBar>
            <Group direction="vertical" horizontalAlign="center" gap={2}>
              <BodyText variant="small" weight="bold">{statusCreditsLabel}</BodyText>
              <Group direction="horizontal" gap={4} verticalAlign="center">
                <BodyText variant="small">
                  <FormattedMessage
                    id="4XTLBdhy0Xunj_vOAlrB"
                    defaultMessage="{value} pending"
                    description="Pending status credits count in loyalty account tile"
                    values={{ value: <FormatDecimal value={pendingPoints.statusCredits} /> }}
                  />
                </BodyText>
                <Tooltip description={statusCreditsPendingTooltip} placement="bottom">
                  <InfoIconWrapper><LineInfoCircleIcon size={14} /></InfoIconWrapper>
                </Tooltip>
              </Group>
            </Group>
          </Group>

          <Group direction="vertical" horizontalAlign="center" gap={4}>
            <LoyaltyAccountTileCircularProgressBar
              progress={nightsProgress}
              tier={tier}
            >
              <Group direction="vertical" horizontalAlign="center" gap={2}>
                <BodyText variant="medium" weight="bold">
                  <FormatDecimal value={currentNights} />
                </BodyText>
                <TierDivider className={`tier-${tier}`} kind="primary" />
                <Caption variant="small">
                  <FormatDecimal value={targetNights} />
                </Caption>
              </Group>
            </LoyaltyAccountTileCircularProgressBar>
            <Group direction="vertical" horizontalAlign="center" gap={2}>
              <BodyText variant="small" weight="bold">{nightsLabel}</BodyText>
              <Group direction="horizontal" gap={4} verticalAlign="center">
                <BodyText variant="small">
                  <FormattedMessage
                    id="m_QJ84bZlYoA2ZzWre3X"
                    defaultMessage="{value} pending"
                    description="Pending nights count in loyalty account tile in the loyalty section"
                    values={{ value: <FormatDecimal value={pendingPoints.nights} /> }}
                  />
                </BodyText>
                <Tooltip description={nightsPendingTooltip} placement="bottom">
                  <InfoIconWrapper><LineInfoCircleIcon size={14} /></InfoIconWrapper>
                </Tooltip>
              </Group>
            </Group>
          </Group>
        </ProgressBarsContainer>

        <VerticalSpacer gap={20}>
          {/* Tier card or congratulations banner */}
          {hasRetainedTier && (
            <LoyaltyDrawerCongratulationsBanner
              tier={tier}
              reviewDate={loyaltyAccountData.statusReviewDate}
            />
          )}
          {!hasRetainedTier && (
            <LoyaltyDrawerTierCard
              tier={unlockCardTier}
              isRetain={effectiveRetainMode}
              retainVariant={retainVariant}
              creditsRemaining={creditsRemaining}
              nightsRemaining={nightsRemaining}
              tierReviewDate={loyaltyAccountData.statusReviewDate}
              membershipYearEndDate={membershipYearEnd}
              showToggle={canToggleView}
              showUpgradeView={showUpgradeView}
              onToggleView={setShowUpgradeView}
              currentTierName={tierName}
              nextTierName={nextTierName}
            />
          )}

          {/* Benefits section */}
          {validBenefitsDisplayInfo && validBenefitsDisplayInfo.length > 0 && (
            <VerticalSpacer gap={8}>
              <BodyText variant="small" weight="bold">{topBenefitsLabel}</BodyText>
              {validBenefitsDisplayInfo.map(({ code, displayInfo }, index) => (
                <React.Fragment key={code}>
                  {index > 0 && <Divider kind="secondary" />}
                  <AccountDrawerBenefitHighlightItem
                    benefit={benefitsMap[code]}
                    tier={tier}
                    title={displayInfo.title}
                    icon={displayInfo.icon}
                  />
                </React.Fragment>
              ))}
            </VerticalSpacer>
          )}

          <Group direction="horizontal" horizontalAlign="end">
            <TextLink size="small">
              {viewMembershipLabel}
            </TextLink>
          </Group>
        </VerticalSpacer>
      </VerticalSpacer>
    </Container>
  </ProductPaletteProvider>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyHasAccountDetailsTile)
